import type { components } from '@feedfm/clips-services-api';
import { ContentCopy } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Tooltip,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudioAuthenticatedContext } from '../../context/StudioContext';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';
import { CollectionThumbnail } from '../CollectionThumbnail';
import { Explicitness } from '../Explicitness';
import { Rating } from '../Rating';
import * as styles from './CollectionCard.styles';
import { TruncatedText } from './TruncatedText';

type Collection = components['schemas']['Collection'];
type CollectionSummary = components['schemas']['CollectionSummary'];
type ArtworkPreSignedUrls = components['schemas']['ArtworkPreSignedUrls'];
type CollectionAndArtwork = components['schemas']['CollectionAndArtwork'];
type CollectionSummaryRow =
  components['schemas']['CollectionsResponse']['collections'][number];

export interface CollectionCardProps {
  artworkPreSignedUrls: ArtworkPreSignedUrls;
  thumbnailUrl?: string | null;
  collection: CollectionSummary | Collection;
}

export const CollectionCardWrapper = ({
  collection,
}: {
  collection: CollectionSummaryRow | CollectionAndArtwork;
}) => (
  <>
    {'summary' in collection && (
      <CollectionCard
        collection={collection.summary}
        artworkPreSignedUrls={collection.artworkPreSignedUrls}
        thumbnailUrl={collection.thumbnailUrl}
      />
    )}
    {'collection' in collection && (
      <CollectionCard
        collection={collection.collection}
        artworkPreSignedUrls={collection.artworkPreSignedUrls}
        thumbnailUrl={collection.thumbnailUrl}
      />
    )}
  </>
);

export const CollectionCard = (props: CollectionCardProps) => {
  const { collection, artworkPreSignedUrls, thumbnailUrl } = props;

  const [isHovered, setIsHovered] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [collectionDetails, setCollectionDetails] = useState<Collection | null>(
    () => ('clips' in collection ? collection : null),
  );
  const navigate = useNavigate();
  const { copyToClipboard } = useCopyToClipboard();
  const { backendClient, playerState, collectionSort } =
    useStudioAuthenticatedContext();

  useEffect(() => {
    if ('clips' in collection) {
      setCollectionDetails(collection);
      return;
    }
    setCollectionDetails(null);
  }, [collection]);

  const handleViewDetails = (collectionId: number) => {
    navigate(`/collections/${collectionId}`, {
      state: { from: '/collections' },
    });
  };

  const handleCopyId = () =>
    copyToClipboard(collection.collectionId.toString(), 'Collection ID');

  const playCollection = (targetCollection: Collection) => {
    playerState.clickCollection(targetCollection, collectionSort);
  };

  const handleThumbnailClick = async () => {
    if (collectionDetails) {
      playCollection(collectionDetails);
      return;
    }

    if (loadingDetails) {
      return;
    }

    setLoadingDetails(true);
    try {
      const detailResponse = await backendClient.collectionById(
        collection.collectionId,
      );
      setCollectionDetails(detailResponse.collection);
      playCollection(detailResponse.collection);
    } catch (error) {
      console.error(
        `Failed to load details for collection ${collection.collectionId}`,
        error,
      );
    } finally {
      setLoadingDetails(false);
    }
  };

  // Check if collection is "new" (published within 30 days)
  const isNew = useMemo(() => {
    if (!collection.publishedOn) {
      return false;
    }

    const publishedDate = new Date(collection.publishedOn);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return publishedDate > thirtyDaysAgo;
  }, [collection.publishedOn]);

  return (
    <Card
      sx={styles.card}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent sx={styles.cardContent}>
        <Box sx={styles.headerContainer}>
          <CollectionThumbnail
            collection={collection}
            artworkPreSignedUrls={artworkPreSignedUrls}
            thumbnailUrl={thumbnailUrl}
            size="medium"
            externalHovered={isHovered}
            showPlayButton
            isLoading={loadingDetails}
            onClick={() => handleThumbnailClick()}
          />
          <Box sx={styles.contentContainer}>
            <Box sx={styles.metadataContainer}>
              {collection.rating && <Rating rating={collection.rating} />}
              {collection.explicit && <Explicitness explicitness="explicit" />}
              {isNew && (
                <Chip
                  label="New"
                  size="small"
                  variant={isHovered ? 'filled' : 'outlined'}
                  sx={isHovered ? styles.newChipFilled : styles.newChip}
                />
              )}
            </Box>
            <Box sx={styles.titleBox}>
              <TruncatedText
                variant="h6"
                color="text.primary"
                sx={styles.title}
                text={collection.collectionName}
              />
            </Box>
          </Box>
        </Box>

        <TruncatedText
          variant="body2"
          color="text.secondary"
          sx={styles.description}
          text={collection.description ?? ''}
          lineClamp={2}
        />

        <Box sx={styles.buttonContainer}>
          <Tooltip
            title="Copy collection ID to clipboard"
            placement="top"
            arrow
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<ContentCopy />}
              onClick={handleCopyId}
              sx={styles.copyButton}
              aria-label={`Copy collection ID ${collection.collectionId} to clipboard`}
            >
              ID: {collection.collectionId}
            </Button>
          </Tooltip>

          <Button
            variant="text"
            size="small"
            onClick={() => handleViewDetails(collection.collectionId)}
            sx={styles.viewDetailsButton}
          >
            View details
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};
